import React, { useState } from 'react';
import Button from 'react-bootstrap/Button';
import noop from 'lodash/noop';
import { ratePercyChat } from '../api/ai';

const STAGE_HELPFUL = 'helpful';
const STAGE_CONTACT = 'contact';
const STAGE_FORM = 'form';
const STAGE_DONE = 'done';

export default function InactivityRatingPrompt({ userEmail = '', messages = [], onDismiss = noop }) {
  const [stage, setStage] = useState(STAGE_HELPFUL);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [contactRequested, setContactRequested] = useState(false);

  function serializeTranscript(msgs) {
    return msgs.map(msg => {
      const time = new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      return `[${time}] ${msg.author}: ${msg.content}`;
    }).join('\n');
  }

  async function handleHelpful(helpful) {
    if (helpful) {
      setSubmitting(true);
      try {
        await ratePercyChat({ helpful: true, contact_requested: false });
      } catch (_) {
        // best-effort — don't block the UI on a rating error
      }
      setSubmitting(false);
      setStage(STAGE_DONE);
    } else {
      // save the "not helpful" rating immediately, then ask about contact
      try {
        await ratePercyChat({ helpful: false, contact_requested: false });
      } catch (_) {
        // best-effort
      }
      setStage(STAGE_CONTACT);
    }
  }

  async function handleContact(wantsContact) {
    if (wantsContact) {
      setStage(STAGE_FORM);
    } else {
      setStage(STAGE_DONE);
    }
  }

  async function handleFormSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await ratePercyChat({ helpful: false, contact_requested: true, note: note.trim(), transcript: serializeTranscript(messages) });
    } catch (_) {
      // best-effort
    }
    setSubmitting(false);
    setContactRequested(true);
    setStage(STAGE_DONE);
  }

  return (
    <div className="percy-rating-prompt">
      {stage === STAGE_HELPFUL && (
        <>
          <p className="percy-rating-prompt__question">Was that helpful today?</p>
          <div className="percy-rating-prompt__buttons">
            <Button
              variant="outline-primary"
              className="chat-button"
              onClick={() => handleHelpful(true)}
              disabled={submitting}
            >
              Yes
            </Button>
            <Button
              variant="outline-secondary"
              className="chat-button"
              onClick={() => handleHelpful(false)}
              disabled={submitting}
            >
              No
            </Button>
          </div>
        </>
      )}

      {stage === STAGE_CONTACT && (
        <>
          <p className="percy-rating-prompt__question">Would you like someone from the team to get in touch?</p>
          <div className="percy-rating-prompt__buttons">
            <Button
              variant="outline-primary"
              className="chat-button"
              onClick={() => handleContact(true)}
            >
              Yes
            </Button>
            <Button
              variant="outline-secondary"
              className="chat-button"
              onClick={() => handleContact(false)}
            >
              No
            </Button>
          </div>
        </>
      )}

      {stage === STAGE_FORM && (
        <form onSubmit={handleFormSubmit} className="percy-rating-prompt__form">
          <p className="percy-rating-prompt__question">We will reach out to you at:</p>
          <p className="percy-rating-prompt__consent">Your conversation with Percy will be shared with our support team.</p>
          <input
            type="email"
            className="form-control percy-rating-prompt__email"
            value={userEmail}
            readOnly
          />
          <textarea
            className="form-control percy-rating-prompt__note"
            placeholder="Anything you want to tell us? (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
          />
          <Button
            type="submit"
            variant="primary"
            className="chat-button percy-rating-prompt__submit"
            disabled={submitting}
          >
            {submitting ? 'Sending...' : 'Submit'}
          </Button>
        </form>
      )}

      {stage === STAGE_DONE && (
        <div className="percy-rating-prompt__thanks">
          <p>Thanks for the feedback.</p>
          {contactRequested && (
            <p>Our team is online Monday to Friday — we'll get back to you as soon as possible.</p>
          )}
        </div>
      )}
    </div>
  );
}
